import React, { useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons';
import { compose } from 'redux';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Divider,
  ListSubheader,
  ListItem as MuiListItem,
  ListItemIcon as MuiListItemIcon,
  ListItemText as MuiListItemText,
  Paper as MuiPaper,
} from '@mui/material';
import { styled, withStyles } from '@mui/styles';
import groupBy from 'lodash/groupBy';

import withRouter from '../../../components/hocs/withRouter';
import { useFillToViewportBottom } from '../../../hooks/useFillToViewportBottom';
import {
  expandedSidebarWidth,
  railSnapX,
} from '../../../components/panels/panelLayout';
import ProtectedComponentBase from '../../../components/main/protected/ProtectedComponent';
import { SidebarSecondaryNav } from './SidebarSecondaryNav';
import { PubSub } from '../../../utils/eventUtils';
import { withShowable } from '../lib/withShowable';

import {
  useIconStyles,
  useItemStyles,
  useItemTextStyles,
  useGroupLabelStyles,
  useSecondaryNavIcon,
} from './styles';

import { BottomNavigation } from './BottomNavigation';
import { RenderMuiListItem } from './RenderMuiListItem';
import { RailTooltip } from './RailTooltip';
import { isOnPath } from './isOnPath';
import { SidebarGroupHeader } from './SidebarGroupHeader';
import { usePersona } from '../../../components/hooks/usePersona';
import { PERSONA_TYPES } from '../../_statics/persona.statics';

const Paper = withShowable(MuiPaper);
const ProtectedComponent = withShowable(ProtectedComponentBase);

const GROUP_LABELS: Record<string, string> = {
  lifecycle: 'Lifecycle',
  admin: 'Admin',
  // The agency rail's groups (personas/broker/agencyRail). They have their own keys, not `manage`, which the
  // policyholder branch owns (see CUSTOMER_GROUP_LABELS).
  brokerDashboard: 'Home',
  agencyResources: 'Resources',
  agencyManage: 'Manage',
};

/**
 * The policyholder's groups, kept out of `GROUP_LABELS` on purpose.
 *
 * That map is what the shared grouped renderer keys off, so a label added there
 * turns the key into an accordion for EVERY persona whose routes happen to carry
 * it -- `resilience` is already noted below as a value Pool's routes carry
 * vestigially. These three are only ever read by the policyholder branch.
 */
const CUSTOMER_GROUP_LABELS: Record<string, string> = {
  insurance: 'Insurance',
  resilience: 'Resiliency Services',
  manage: 'Manage',
};

/** The group Manage's own pages are rendered under, wherever they come from. */
const CUSTOMER_MANAGE_GROUP = 'manage';

/** Groups that open the list. With nothing above them, a divider would only draw a line along the card's top edge. */
const GROUPS_THAT_OPEN_THE_LIST: ReadonlySet<string> = new Set([
  'brokerDashboard',
]);

/** Whether a divider goes before a group's header: not for a group that opens the list. */
export const groupDividerWanted = (
  group: string,
  nodesAbove: number
): boolean => nodesAbove > 0 || !GROUPS_THAT_OPEN_THE_LIST.has(group);

/** A group renders as an accordion only where its persona has named it. */
const groupLabelFor = (group?: string): string | undefined =>
  group ? GROUP_LABELS[group] ?? CUSTOMER_GROUP_LABELS[group] : undefined;

// Persist the groups the user opened or closed (and those a deep link opened) next to the rail preference
// (`cowbellSidebarRailCollapsed`), so they survive a reload. Only those choices are stored: the defaults below are
// worked out on every load. The key is new because the one before it stored every group, defaults included, so a
// default could never change for anyone who had loaded the sidebar once.
const GROUP_CHOICES_STORAGE_KEY = 'cowbellSidebarGroupChoices';

/** A window this short has no room for the agency rail's Resources and Manage open, so there they start collapsed. */
export const SHORT_WINDOW_QUERY = '(max-height: 45rem)';

/** Which groups start collapsed: Admin always; the agency rail's Resources and Manage only in a short window. */
export const defaultCollapsedGroups = (
  shortWindow: boolean
): Record<string, boolean> => ({
  admin: true,
  agencyResources: shortWindow,
  agencyManage: shortWindow,
});

const isShortWindow = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(SHORT_WINDOW_QUERY).matches;

/** Whether the window is short, kept current as it is resized. */
function useShortWindow(): boolean {
  const [short, setShort] = useState(isShortWindow);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const query = window.matchMedia(SHORT_WINDOW_QUERY);
    const onChange = () => setShort(query.matches);
    query.addEventListener?.('change', onChange);
    return () => query.removeEventListener?.('change', onChange);
  }, []);
  return short;
}

/** The parts of a route's `nav` the selection rule reads. */
interface SidebarNav {
  readonly href?: string;
  readonly selectWhen?: 'exact';
  /** More paths the item is selected on, for an item that stands for several pages (Users: users, teams, roles). */
  readonly selectAlso?: readonly string[];
}

/**
 * Whether a nav item is the open page. An external link (`nav.href`) never is. An item that sets
 * `selectWhen: 'exact'` is selected on its own path or anything under it, or under any of its `selectAlso` paths; every
 * other item keeps the original rule, which compares the first three segments of the URL and so selects every sibling
 * that shares them.
 */
const isNavItemSelected = (
  nav: SidebarNav | undefined,
  to: string,
  pathname: string,
  finalURL: string
): boolean => {
  if (nav?.href) {
    return false;
  }
  if (nav?.selectWhen === 'exact') {
    // A link may carry a query (a dashboard tab's keeps the search); the page is chosen by the path alone.
    const path = (to.split('/:').shift() as string).split('?')[0];
    return (
      isOnPath(pathname, path) ||
      (nav.selectAlso ?? []).some((also) => isOnPath(pathname, also))
    );
  }
  return to.startsWith(finalURL);
};

/**
 * Groups whose open or closed state is never remembered. Admin is a place staff visit, not one they work from: opened
 * once, or opened by a deep link to an admin page, it stayed open on every load after. It now starts closed every time
 * and opens only while the user is on one of its pages or opens it themselves.
 */
const UNREMEMBERED_GROUPS = ['admin'];

/** The stored choices without the groups that always start from their default. */
export const rememberedGroupChoices = (
  choices: Record<string, boolean>
): Record<string, boolean> =>
  Object.fromEntries(
    Object.entries(choices).filter(
      ([group]) => !UNREMEMBERED_GROUPS.includes(group)
    )
  );

const readGroupChoices = (): Record<string, boolean> => {
  try {
    const raw = localStorage.getItem(GROUP_CHOICES_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return rememberedGroupChoices(parsed as Record<string, boolean>);
      }
    }
  } catch {
    // malformed or unavailable storage — fall back to the defaults.
  }
  return {};
};

const writeGroupChoices = (groups: Record<string, boolean>): void => {
  try {
    localStorage.setItem(
      GROUP_CHOICES_STORAGE_KEY,
      JSON.stringify(rememberedGroupChoices(groups))
    );
  } catch {
    // storage unavailable (private mode / quota) — non-fatal.
  }
};

const styles = ({ breakpoints, shape, config, palette }: any) => ({
  sidebar: {
    width: 'inherit',
    // From where it starts (measured, see useFillToViewportBottom) to the
    // bottom of the window, less the gutter the page leaves below its tiles.
    // 6.4rem was the start in production only; the "Not Production" strip
    // pushed it down, so it ran off the bottom of the window.
    height: 'calc(100dvh - var(--fill-top, 6.4rem) - var(--tile-gutter, 0rem))',
    position: 'fixed',
    zIndex: 10,
    borderRadius: 'var(--tile-radius)',
    transition: 'left 400ms ease-in-out',
    overflow: 'auto',
    // This shell is the tile, not the Paper it scrolls. Its corners are the
    // ones that stay rounded at every scroll position: the Paper's own scroll
    // away with it, and this shell's rounding falls on the scrollbar column,
    // so with the page showing through, the top-right corner went square once
    // the menu scrolled. Painted the Paper's colour, the scrollbar sits inside
    // the tile, its track kept clear of the rounded corners.
    backgroundColor: palette.background.paper,

    '&::-webkit-scrollbar-track': {
      marginBlock: 'var(--tile-radius)',
    },

    [breakpoints.down('sm')]: {
      height: '100%',
      top: 75,
      left: '-100%',

      '&.is-open': {
        left: 0,
      },
    },
  },

  links: {
    // Fills the flex-column Paper (grows to content when a group expands);
    // space-between pins the footer to the bottom when the menu is short.
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },

  // Icon-only rail: hide labels/chevrons and centre every icon, for BOTH the
  // main grouped nav and the BottomNavigation below it (one class on their
  // shared container). `& .MuiListItem-root > svg` targets the flyout/expand
  // chevrons — a direct child of the row — never the nav icon, which is nested
  // inside .MuiListItemIcon-root, so the glyphs survive and only chevrons hide.
  railCollapsed: {
    '& .MuiListItemText-root': {
      display: 'none',
    },
    '& .MuiListItem-root': {
      justifyContent: 'center',
      paddingLeft: 0,
      paddingRight: 0,
    },
    // Scoped under the row so it outranks `compactNav`'s icon marginRight,
    // which sits later in this sheet at the same specificity and otherwise
    // pushes every rail icon left of centre.
    '& .MuiListItem-root .MuiListItemIcon-root': {
      marginRight: 0,
    },
    '& .MuiListItem-root > svg': {
      display: 'none',
    },
  },

  // White surface must fill the fixed shell when the menu is short (no grey
  // gap) AND grow to cover every row when a group expands (otherwise tall
  // content spills below a fixed-height Paper onto the grey page). min-height +
  // flex column does both; the shell's own `overflow: auto` scrolls the excess.
  paper: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100%',
    // The platform tile radius, like every tile beside it.
    borderRadius: 'var(--tile-radius)',
  },

  // Draggable right-edge grip — drag it to collapse/expand the menu.
  resizeHandle: {
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    width: 6,
    cursor: 'col-resize',
    zIndex: 11,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'none',
    padding: 0,
    background: 'transparent',

    '&:hover': {
      backgroundColor: config.colors.cowbellBlue,
      opacity: 0.25,
    },

    // Surface the chevron affordance on hover or keyboard focus so the collapse
    // control is discoverable (and give the focused grip a visible ring).
    '&:hover $resizeChevron, &:focus-visible $resizeChevron': {
      opacity: 1,
    },
    '&:focus-visible': {
      outline: `2px solid ${config.colors.cowbellBlue}`,
      outlineOffset: -2,
    },
  },

  // A small chevron centred on the grip, hidden until the grip is hovered or
  // focused. Nudged left so it sits over the sidebar edge rather than the 6px
  // hit-area, and non-interactive so it never eats the grip's own events.
  resizeChevron: {
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '1.25rem',
    height: '1.25rem',
    borderRadius: '50%',
    backgroundColor: config.colors.cowbellBlue,
    color: '#fff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.25)',
    opacity: 0,
    transition: 'opacity 150ms ease',
    pointerEvents: 'none',

    '& svg': {
      fontSize: '1rem',
    },
  },

  // The mark after an external link's name: smaller than the name, in the rail's muted icon ink, level with the middle
  // of the line, and kept with the last word. `&&& svg` outranks the row's hover rule, which fills every svg in it.
  externalMark: {
    display: 'inline-block',
    marginLeft: '0.35em',
    fontSize: '0.75em',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    color: config.colors.darkGrey3,

    '&&& svg': {
      fill: config.colors.darkGrey3,
      color: config.colors.darkGrey3,
    },
  },

  // The compact nav icon treatment: a uniform 1.25rem box, one soft-grey ink
  // for filled and stroked glyphs alike, and a guard so the shared
  // selected/hover `fill` cannot flood the outline (line) icons solid.
  //
  // Applied to the Cowbell and Policyholder sidebars. Every other persona still
  // gets the base `useIconStyles`, whose glyphs are 2.0833rem inside MUI's
  // default 56px ListItemIcon gutter -- next to Cowbell's, those icons read as
  // oversized and far too widely spaced.
  compactNav: {
    // Tighter rows than the shared item padding (0.85rem all round); the sides stay as they are. Only the nav links:
    // group headers and the footer's `.small` rows keep their own padding.
    '& [data-qa^="SIDEBAR_NAVLINK:"]:not(.small *)': {
      paddingTop: '0.65rem',
      paddingBottom: '0.65rem',
    },

    '& .MuiListItemIcon-root': {
      minWidth: 'unset',
      width: '1.25rem',
      height: '1.25rem',
      marginRight: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      color: config.colors.darkGrey3,
    },

    '& .MuiListItemIcon-root svg': {
      width: '1.25rem',
      height: '1.25rem',
      maxWidth: '100%',
      display: 'block',
    },

    '& .MuiListItemIcon-root svg[fill="none"]': {
      fill: 'none !important',
    },
  },

  buttonOutlined: {
    width: '100%',
    borderWidth: 2,
    borderColor: config.colors.cowbellBlue,
    color: config.colors.cowbellBlue,
    fontSize: '1rem',
    fontWeight: 'bold',

    '&:hover': {
      borderWidth: 2,
      backgroundColor: '#E4E8EC',
    },
  },

  buttonFilled: {
    width: '100%',
    backgroundColor: config.colors.lightGrey4,
    fontSize: '1rem',
    fontWeight: 'bold',
    color: config.colors.cowbellBlue,
    border: `2px solid ${config.colors.cowbellBlue}`,

    '&:hover': {
      borderWidth: 2,
      backgroundColor: config.colors.lightGrey4,
    },
  },
});

export const Sidebar = compose(
  // @ts-expect-error TS(2345): Argument of type '({ breakpoints, shape }: any) =>... Remove this comment to see the full error message
  withStyles(styles),
  withRouter
)(
  ({
    location,
    classes,
    routes = [],
    rootPath,
    history,
    railCollapsed = false,
    toggleRail,
    setRail,
  }: any) => {
    const [open, setOpen] = useState(false);
    const shortWindow = useShortWindow();
    const [groupChoices, setGroupChoices] =
      useState<Record<string, boolean>>(readGroupChoices);
    // The user's own choices win; every other group starts as the window's height says.
    const collapsedGroups = React.useMemo(
      () => ({ ...defaultCollapsedGroups(shortWindow), ...groupChoices }),
      [shortWindow, groupChoices]
    );
    const itemClasses = useItemStyles();
    const itemIconClasses = useIconStyles();
    const itemTextClasses = useItemTextStyles();
    const groupLabelClasses = useGroupLabelStyles();
    const secondaryIconClasses = useSecondaryNavIcon();
    const persona = usePersona();
    const sidebarRef = React.useRef<HTMLDivElement>(null);
    // Ends on the same line as the page beside it, wherever it starts.
    useFillToViewportBottom(sidebarRef);
    // Set while an actual drag occurs so the trailing click doesn't also toggle.
    const didDragRef = React.useRef(false);
    // Holds the active drag's teardown so unmounting mid-drag can't leak the
    // document listeners (they'd keep calling setRail on a dead component).
    const dragCleanupRef = React.useRef<(() => void) | null>(null);

    // Drag the sidebar's right edge to collapse/expand: as the pointer crosses
    // the midpoint between the rail and the expanded width, snap to whichever
    // side it's nearer. Cowbell-only (setRail is undefined elsewhere).
    const startRailResize = (event: React.MouseEvent) => {
      if (typeof setRail !== 'function' || !sidebarRef.current) {
        return;
      }
      event.preventDefault();
      didDragRef.current = false;
      const { left } = sidebarRef.current.getBoundingClientRect();
      // The widths are in rem, so the midpoint moves with the root font size.
      const rootFontPx =
        parseFloat(getComputedStyle(document.documentElement).fontSize) || 12;
      // The expanded width in force: the agency rail publishes a wider one than the default over `--sidebar-width`.
      const expanded = expandedSidebarWidth(
        getComputedStyle(document.documentElement).getPropertyValue(
          '--sidebar-width'
        )
      );
      const snapX = railSnapX(rootFontPx, expanded);
      const onMove = (moveEvent: MouseEvent) => {
        didDragRef.current = true;
        setRail(moveEvent.clientX - left < snapX);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
        dragCleanupRef.current = null;
      };
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      dragCleanupRef.current = onUp;
    };

    // Tear down any in-progress drag if the sidebar unmounts mid-drag.
    useEffect(() => () => dragCleanupRef.current?.(), []);

    // A plain click on the grip toggles the rail — the reliable, discoverable
    // alternative to the finicky drag. A click that concluded a drag is ignored
    // (the drag already applied the collapse/expand).
    const handleRailToggleClick = () => {
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      if (typeof toggleRail === 'function') {
        toggleRail();
      }
    };

    // Keyboard access for the grip (it is a focusable button): Enter/Space
    // toggle, Left collapses to the rail, Right expands.
    const handleRailKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (typeof toggleRail === 'function') {
          toggleRail();
        }
      } else if (event.key === 'ArrowLeft' && typeof setRail === 'function') {
        setRail(true);
      } else if (event.key === 'ArrowRight' && typeof setRail === 'function') {
        setRail(false);
      }
    };

    const toggleGroup = (name: string) => {
      setGroupChoices((current) => ({
        ...current,
        [name]: !collapsedGroups[name],
      }));
    };

    const sidebarClasses = classnames(classes.sidebar, { 'is-open': open });

    const filtered = useMemo(
      () => routes.filter((route: any) => !!route.nav),
      [routes]
    );

    useEffect(() => {
      const closesub = PubSub.subscribe('mobile-menu:close', () => {
        setOpen(false);
      });

      const togglesub = PubSub.subscribe('mobile-menu:toggle', () => {
        setOpen((current) => !current);
      });

      return () => {
        closesub.remove();
        togglesub.remove();
      };
      // eslint-disable-next-line
    }, []);

    const handleClick = () => {
      setOpen(false);
    };

    const splitTab = location.pathname
      .split('/')
      .slice(0, persona.isInsured ? 4 : 3);
    const finalURL = splitTab.join('/').split('/:').shift();
    const support =
      React.useMemo(
        () => _.find(routes, { path: `/${rootPath}/support/:tab` }),
        [rootPath, routes]
      ) || {};

    const group = groupBy(filtered, 'nav.group');

    // A deep-link into a collapsed group (e.g. Admin, collapsed by default) would
    // otherwise leave the page with no visible selected nav item. Auto-expand
    // whichever group owns the active route so its selected item is always shown.
    // Manage's pages hang off a route's `subroutes`, not off `filtered`, and their
    // parent is grouped `other` -- so searching top-level routes alone never found
    // Org. Profile, Users or My Profile, and deep-linking to one of them with
    // Manage collapsed left the page with no visible selected item.
    const groupedRoutes = useMemo(
      () =>
        filtered.flatMap((route: any) => {
          const own = { path: route.path, nav: route.nav };
          const children = (route.subroutes ?? [])
            .filter((child: any) => !!child.nav?.name)
            .map((child: any) => ({
              path: child.path,
              nav: { ...child.nav, group: CUSTOMER_MANAGE_GROUP },
            }));

          return [own, ...children];
        }),
      [filtered]
    );

    // Items that match on the exact path need the whole pathname to find the active group; the rest still need only
    // the first three segments, so the effect below re-runs on the same changes it always did.
    const usesExactMatch = groupedRoutes.some(
      (route: any) => route.nav?.selectWhen === 'exact'
    );
    const activeKey = usesExactMatch ? location.pathname : finalURL;

    useEffect(() => {
      const activeGroup = groupedRoutes.find((route: any) => {
        const to = route.nav?.target || route.path;
        return (
          typeof to === 'string' &&
          !!finalURL &&
          isNavItemSelected(route.nav, to, location.pathname, finalURL)
        );
      })?.nav?.group;
      if (groupLabelFor(activeGroup)) {
        if (collapsedGroups[activeGroup]) {
          setGroupChoices((prev) => ({ ...prev, [activeGroup]: false }));
        }
      }
      // `activeKey` stands in for `finalURL` and `location.pathname`, so the effect runs when either could change
      // the answer and not on every navigation. `shortWindow` is here because a window resized short collapses the
      // groups by default, and the one holding the open page must stay open.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeKey, groupedRoutes, shortWindow]);

    // Persist the accordion open/closed state alongside the rail preference, so a
    // group the user opened (or that auto-expanded for a deep link) survives a
    // reload instead of snapping back to the default. This also hardens the
    // deep-link auto-expand: if the effect above were ever to miss its window,
    // the last-known-open state still restores the group.
    useEffect(() => {
      writeGroupChoices(groupChoices);
    }, [groupChoices]);

    if (persona.type === PERSONA_TYPES.CUSTOMER) {
      // Manage's own pages, promoted out of the footer. It used to be a single row
      // down beside Support with its four pages behind a flyout, which put an
      // account's own settings a hover away and below the version number.
      const manageRoutes = (
        _.find(routes, (route: any) => route.nav?.name === 'Manage')
          ?.subroutes ?? []
      ).filter((route: any) => !!route.nav?.name);

      const renderCustomerGroup = (groupKey: string, groupRoutes: any[]) => {
        if (!Array.isArray(groupRoutes) || groupRoutes.length === 0) {
          return null;
        }

        const isCollapsed = !!collapsedGroups[groupKey];
        // `nav.groupOrder` decides the order within a group. Routes reach here in
        // whatever order their definitions happen to sit in, which is source
        // order across two files for the Manage group.
        const ordered = [...groupRoutes].sort(
          (a: any, z: any) =>
            (a.nav?.groupOrder ?? 0) - (z.nav?.groupOrder ?? 0)
        );

        return (
          <div key={groupKey}>
            {railCollapsed ? (
              // No room for a labelled, toggleable header in the rail -- mark
              // the boundary with a divider, exactly as the Cowbell sidebar does,
              // and show every destination as an icon. Honouring the collapsed
              // accordion here would hide rows with no chevron to bring them back.
              <Divider />
            ) : (
              <SidebarGroupHeader
                label={CUSTOMER_GROUP_LABELS[groupKey]}
                group={groupKey}
                isCollapsed={isCollapsed}
                onToggle={toggleGroup}
              />
            )}
            {(railCollapsed || !isCollapsed) &&
              ordered.map((route: any) => {
                const to = route.nav.target || route.path;

                return (
                  <RenderMuiListItem
                    key={to}
                    route={route}
                    to={to}
                    finalURL={finalURL}
                    handleClick={handleClick}
                    railCollapsed={railCollapsed}
                  />
                );
              })}
          </div>
        );
      };

      return (
        <div className={sidebarClasses} ref={sidebarRef}>
          {/* The same right-edge grip the Cowbell sidebar has: click or keyboard
            toggles, drag snaps to whichever side the pointer is nearer. */}
          {typeof setRail === 'function' && (
            <div
              className={classes.resizeHandle}
              role="button"
              tabIndex={0}
              aria-label={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              onMouseDown={startRailResize}
              onClick={handleRailToggleClick}
              onKeyDown={handleRailKeyDown}
              data-qa="SIDEBAR_RAIL_RESIZE"
            >
              <span className={classes.resizeChevron} aria-hidden="true">
                {railCollapsed ? (
                  <ChevronRightIcon fontSize="small" />
                ) : (
                  <ChevronLeftIcon fontSize="small" />
                )}
              </span>
            </div>
          )}
          <Paper elevation={6} className={classes.paper}>
            {/* Same compact icon treatment as the Cowbell sidebar. Without it
              this persona falls back to the base 2.0833rem glyphs sitting in
              MUI's default 56px gutter, which read as oversized and far too
              widely spaced next to every other sidebar in the product. */}
            <div
              className={classnames(classes.links, classes.compactNav, {
                [classes.railCollapsed]: railCollapsed,
              })}
            >
              <div>
                {Array.isArray(group?.dashboard) &&
                  group.dashboard.length > 0 &&
                  group.dashboard.map((route) => {
                    const to = route.nav.target || route.path;

                    return (
                      <RenderMuiListItem
                        key={to}
                        route={route}
                        to={to}
                        finalURL={finalURL}
                        handleClick={handleClick}
                        railCollapsed={railCollapsed}
                      />
                    );
                  })}
                {renderCustomerGroup('insurance', group?.insurance)}
                {renderCustomerGroup('resilience', group?.resilience)}
                {renderCustomerGroup('manage', [
                  ...manageRoutes,
                  ...(group?.manage ?? []),
                ])}
              </div>

              <BottomNavigation
                location={location}
                handleClick={handleClick}
                rootPath={rootPath}
                support={support}
                railCollapsed={railCollapsed}
              />
            </div>
          </Paper>
        </div>
      );
    }

    const renderNavItem = ({ nav, ...route }: any) => {
      const to = nav.target || route.path;
      if (to.includes('/customer/resilience')) {
        return null;
      }

      if (route.subroutes) {
        return (
          <ProtectedComponent
            allow={route.allow}
            allowEither={route.allowEither}
            allowIfAll={route.allowIfAll}
            key={to}
          >
            <SidebarSecondaryNav
              routes={route.subroutes}
              parent={nav.name}
              railCollapsed={railCollapsed}
            >
              <MuiListItem
                data-submenu
                button
                classes={itemClasses}
                selected={location.pathname.indexOf(to) !== -1}
                component="a"
                data-qa={`SIDEBAR_NAVLINK:${nav.name}`}
              >
                <MuiListItemIcon classes={itemIconClasses}>
                  {nav.icon}
                </MuiListItemIcon>

                <MuiListItemText
                  classes={itemTextClasses}
                  disableTypography
                  secondary={nav.name}
                />

                <ChevronRightIcon classes={secondaryIconClasses} />
              </MuiListItem>
            </SidebarSecondaryNav>
          </ProtectedComponent>
        );
      }

      const selected = isNavItemSelected(nav, to, location.pathname, finalURL);
      const routeLink = (
        <MuiListItem
          button
          classes={itemClasses}
          selected={selected}
          aria-current={selected ? 'page' : undefined}
          component={Link}
          to={to.split('/:').shift()}
          onClick={() => handleClick()}
          data-qa={`SIDEBAR_NAVLINK:${nav.name}`}
        >
          {typeof nav.Component === 'function' ? (
            <nav.Component
              icon={nav.icon}
              itemTextClasses={itemTextClasses}
              name={nav.name}
              itemIconClasses={itemIconClasses}
            />
          ) : (
            <>
              <MuiListItemIcon classes={itemIconClasses}>
                {nav.icon}
              </MuiListItemIcon>
              <MuiListItemText
                classes={itemTextClasses}
                disableTypography
                secondary={nav.name}
              />
            </>
          )}
        </MuiListItem>
      );

      // An external link (`nav.href`) opens in a new tab, is never selected, and carries the console's external-link
      // icon, which the reader sees and a screen reader skips.
      const externalLink = (
        <MuiListItem
          button
          classes={itemClasses}
          component="a"
          href={nav.href}
          target="_blank"
          rel="noopener noreferrer"
          data-qa={`SIDEBAR_NAVLINK:${nav.name}`}
        >
          <MuiListItemIcon classes={itemIconClasses}>
            {nav.icon}
          </MuiListItemIcon>
          <MuiListItemText
            classes={itemTextClasses}
            disableTypography
            secondary={
              <>
                {nav.name}
                <span className={classes.externalMark} aria-hidden="true">
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </span>
              </>
            }
          />
        </MuiListItem>
      );
      const item = nav.href ? externalLink : routeLink;

      return (
        <ProtectedComponent
          allow={route.allow}
          allowEither={route.allowEither}
          key={to}
        >
          <RailTooltip railCollapsed={railCollapsed} title={nav.name}>
            {item}
          </RailTooltip>
        </ProtectedComponent>
      );
    };

    const renderGroupedNav = () => {
      const nodes: React.ReactNode[] = [];
      let currentGroup: string | undefined;

      filtered.forEach(({ nav, ...route }: any) => {
        // Only nav.group values with a registered label render as an
        // accordion section. Anything else (undefined, or a stray/vestigial
        // value another persona's routes happen to carry, e.g. Pool's
        // `resilience`) renders inline exactly as before grouping existed.
        const groupName: string | undefined = GROUP_LABELS[nav.group]
          ? nav.group
          : undefined;

        if (groupName && groupName !== currentGroup) {
          const divider = groupDividerWanted(groupName, nodes.length) ? (
            <Divider key={`group-divider-${groupName}`} />
          ) : null;
          if (railCollapsed) {
            // No room for a labelled, toggleable header in the rail — mark the
            // group boundary with a divider; every item below shows as an icon.
            if (divider) {
              nodes.push(divider);
            }
          } else {
            const isCollapsed = !!collapsedGroups[groupName];

            nodes.push(
              divider,
              <SidebarGroupHeader
                key={`group-header-${groupName}`}
                label={GROUP_LABELS[groupName]}
                group={groupName}
                isCollapsed={isCollapsed}
                onToggle={toggleGroup}
              />
            );
          }
        }

        currentGroup = groupName;

        // The rail shows every destination as an icon (there's no visible chevron
        // to re-open an accordion group), so only honour the collapsed-accordion
        // skip when the sidebar is expanded.
        if (!railCollapsed && groupName && collapsedGroups[groupName]) {
          return;
        }

        nodes.push(renderNavItem({ nav, ...route }));
      });

      return nodes;
    };

    return (
      <div className={sidebarClasses} ref={sidebarRef}>
        {/* Collapse/expand control on the right edge (Cowbell-only): click or
          keyboard toggles, drag snaps, and a chevron surfaces on hover/focus so
          the affordance is discoverable. */}
        {typeof setRail === 'function' && (
          <div
            className={classes.resizeHandle}
            role="button"
            tabIndex={0}
            aria-label={railCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            onMouseDown={startRailResize}
            onClick={handleRailToggleClick}
            onKeyDown={handleRailKeyDown}
            data-qa="SIDEBAR_RAIL_RESIZE"
          >
            <span className={classes.resizeChevron} aria-hidden="true">
              {railCollapsed ? (
                <ChevronRightIcon fontSize="small" />
              ) : (
                <ChevronLeftIcon fontSize="small" />
              )}
            </span>
          </div>
        )}

        <Paper elevation={6} className={classes.paper}>
          <div
            className={classnames(classes.links, {
              // The Cowbell shell supplies a rail toggle; the policyholder shell
              // is identified by its rootPath, which holds whether the viewer is
              // a policyholder or a Cowbell user browsing that area.
              [classes.compactNav]:
                typeof toggleRail === 'function' || rootPath === 'customer',
              [classes.railCollapsed]: railCollapsed,
            })}
          >
            {/* The rail is driven by the right-edge grip (click, drag, or
              keyboard) — see the resizeHandle above; no in-column toggle so it
              costs no vertical space. */}
            <div>{renderGroupedNav()}</div>

            <BottomNavigation
              location={location}
              handleClick={handleClick}
              rootPath={rootPath}
              support={support}
              railCollapsed={railCollapsed}
            />
          </div>
        </Paper>
      </div>
    );
  }
);

// marginTop clears MainHeader (position: fixed). The app-wide 5.4rem "clear
// the header" constant is rem-based, but the header is content-sized (~67px)
// and doesn't scale with the root font — at this app's 12px root, 5.4rem is
// only 64.8px, ~2px SHORT of the header, so the collapsed 2px line ended up
// hidden behind it. The small fixed-px nudge lands the line flush against the
// header's bottom edge (connected, no gap) while staying fully visible at any
// root font-size.
export const EnvironmentIndicator = styled(Paper)({
  width: '100%',
  marginTop: 'calc(5.4rem + 3px)',
  padding: '5px 8px',
  zIndex: 999,
  background: '#9a50d3',
  color: '#fff',
  borderRadius: 0,
  display: 'flex',
  alignItems: 'center',
});
